// **Home's gradient veil, as data** (`client-home`; design **D-h**).
//
// The veil is a **gradient, not a wash**: near-clear at the top where the hive and the densest bee
// traffic sit, thickening downward under the controls, with the join and create surfaces drawn on
// **opaque** cards rather than translucent ones. `background-legibility.html` settles why. Panel B
// — a flat paper wash — passes contrast handily and is rejected anyway, because a uniform veil
// buys legibility by killing the aliveness that justifies having a background at all. Panel A —
// no veil — is rejected on *collision* (flowers and drifting bees crossing the wordmark) rather
// than on contrast, which it misses only marginally.
//
// The stops live here as **numbers**, and the CSS the screen renders is built from them, so a test
// can measure the veil the player actually sees. A contrast helper over hand-copied colour
// constants would be measuring a comment.
//
// ## What this can and cannot assert
//
// It computes WCAG 2.x relative luminance over the veil's **declared stops** composited on the
// field's own gradient, at each label's band. That is a real property and it binds
// `client-home/The veil's declared stops meet AA against the text tokens`.
//
// It is **not** a claim about rendered pixels. `jsdom` performs no layout, rasterizes no CSS
// gradient and renders no Pixi canvas, so a helper here cannot see a bee sprite passing under the
// wordmark — and the sketch documents exactly that collision as the reason panel A was rejected.
// `client-home/Rendered frames meet AA contrast` is therefore excused in-spec and discharged by
// browser-captured evidence under `docs/dev/evidence/home-screen/`, not by anything in this file
// (artifacts review **B3**).

/** A colour stop: `position` along the gradient in `0..1`, and the alpha of the paper veil there. */
export interface VeilStop {
  position: number;
  alpha: number;
}

/** The veil's paper colour — `--paper-0`, the kit's one warm-paper surface. */
export const VEIL_PAPER: readonly [number, number, number] = [253, 249, 239];

/** The chosen radial veil (panel C). An ellipse `120% × 74%` centred at `50% 26%`, so along the
 * vertical centre line the gradient's extent is `0.74` of the screen height from a centre `0.26`
 * of the way down. */
export const VEIL_STOPS: readonly VeilStop[] = [
  { position: 0, alpha: 0 },
  { position: 0.58, alpha: 0.42 },
  { position: 1, alpha: 0.93 },
];

export const VEIL_CENTRE_Y = 0.26;
export const VEIL_EXTENT_Y = 0.74;

/**
 * **The header scrim** — a second, local paper layer behind the wordmark and tagline only.
 *
 * Added after the rendered-pixel evidence contradicted the declared-stops model, which is exactly
 * what that evidence exists for. The sketch tuned the veil against a *flower-speckled gradient*
 * ground; the shipped renderer draws a flat, considerably brighter summer quad with pixel-art bee
 * and flower sprites on it, and a dark sprite drifting under the tagline took the measured worst
 * case to **1.97:1** — a real AA failure that no `jsdom` helper could have seen (artifacts review
 * **B3**, vindicated). The captured plates and the numbers are under
 * `docs/dev/evidence/home-screen/`.
 *
 * The fix is deliberately **local** rather than a thicker global veil, because thickening the veil
 * is precisely panel B — the flat wash that passes contrast handily and is rejected anyway for
 * killing the aliveness that justifies having a background. **D-h**'s rule is "the field stays
 * vivid exactly where nothing is written over it"; a scrim scoped to the band where something *is*
 * written over it is that rule applied more sharply, not abandoned. It is gone by 24% of the
 * screen height, well above the hive.
 */
export const HEADER_SCRIM_STOPS: readonly VeilStop[] = [
  { position: 0, alpha: 0.68 },
  { position: 0.13, alpha: 0.65 },
  { position: 0.17, alpha: 0.32 },
  { position: 0.24, alpha: 0 },
];

/** The static season-ground fill the background degrades to, and the colour the live loop's
 * summer ground draws — the field the veil is composited over. */
export const FIELD_STOPS: readonly { position: number; rgb: readonly [number, number, number] }[] = [
  { position: 0, rgb: [185, 206, 142] },
  { position: 0.46, rgb: [168, 193, 121] },
  { position: 1, rgb: [147, 174, 102] },
];

/** The veil as the CSS the screen renders. Built from {@link VEIL_STOPS} so the measured value and
 * the drawn value cannot drift apart. */
export function veilCss(): string {
  const stops = VEIL_STOPS.map(
    (stop) =>
      `rgba(${VEIL_PAPER[0]},${VEIL_PAPER[1]},${VEIL_PAPER[2]},${stop.alpha}) ${Math.round(stop.position * 100)}%`,
  ).join(', ');
  return `radial-gradient(120% ${Math.round(VEIL_EXTENT_Y * 100)}% at 50% ${Math.round(
    VEIL_CENTRE_Y * 100,
  )}%, ${stops})`;
}

/** The header scrim as the CSS the screen renders, built from the same stops it is measured at. */
export function headerScrimCss(): string {
  const stops = HEADER_SCRIM_STOPS.map(
    (stop) =>
      `rgba(${VEIL_PAPER[0]},${VEIL_PAPER[1]},${VEIL_PAPER[2]},${stop.alpha}) ${Math.round(stop.position * 100)}%`,
  ).join(', ');
  return `linear-gradient(180deg, ${stops})`;
}

/** The field fill as the CSS the degraded background renders. */
export function fieldCss(): string {
  const stops = FIELD_STOPS.map(
    (stop) => `rgb(${stop.rgb[0]},${stop.rgb[1]},${stop.rgb[2]}) ${Math.round(stop.position * 100)}%`,
  ).join(', ');
  return `linear-gradient(180deg, ${stops})`;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Samples a stop list at `t`, clamping past its ends. */
function alphaAlong(stops: readonly VeilStop[], t: number): number {
  for (let i = 1; i < stops.length; i += 1) {
    const from = stops[i - 1]!;
    const to = stops[i]!;
    if (t <= to.position) {
      const span = to.position - from.position;
      return span === 0 ? to.alpha : lerp(from.alpha, to.alpha, (t - from.position) / span);
    }
  }
  return stops[stops.length - 1]!.alpha;
}

/** The veil's alpha at a vertical screen fraction `y` (0 = top, 1 = bottom), on the centre line. */
export function veilAlphaAt(y: number): number {
  return alphaAlong(VEIL_STOPS, Math.min(1, Math.abs(y - VEIL_CENTRE_Y) / VEIL_EXTENT_Y));
}

/** The header scrim's alpha at a vertical screen fraction `y`. Zero below its last stop. */
export function headerScrimAlphaAt(y: number): number {
  return alphaAlong(HEADER_SCRIM_STOPS, Math.min(1, Math.max(0, y)));
}

/** The two paper layers composited together, as one effective alpha. */
export function paperAlphaAt(y: number): number {
  return 1 - (1 - veilAlphaAt(y)) * (1 - headerScrimAlphaAt(y));
}

/** The field's colour at a vertical screen fraction `y`. */
export function fieldColourAt(y: number): [number, number, number] {
  const t = Math.min(1, Math.max(0, y));
  for (let i = 1; i < FIELD_STOPS.length; i += 1) {
    const from = FIELD_STOPS[i - 1]!;
    const to = FIELD_STOPS[i]!;
    if (t <= to.position) {
      const span = to.position - from.position;
      const k = span === 0 ? 1 : (t - from.position) / span;
      return [
        lerp(from.rgb[0], to.rgb[0], k),
        lerp(from.rgb[1], to.rgb[1], k),
        lerp(from.rgb[2], to.rgb[2], k),
      ];
    }
  }
  const last = FIELD_STOPS[FIELD_STOPS.length - 1]!;
  return [last.rgb[0], last.rgb[1], last.rgb[2]];
}

/** The colour behind a label at vertical fraction `y`: both paper layers over the field. */
export function veiledBackgroundAt(y: number): [number, number, number] {
  const alpha = paperAlphaAt(y);
  const field = fieldColourAt(y);
  return [
    VEIL_PAPER[0] * alpha + field[0] * (1 - alpha),
    VEIL_PAPER[1] * alpha + field[1] * (1 - alpha),
    VEIL_PAPER[2] * alpha + field[2] * (1 - alpha),
  ];
}

/** Parses `#rrggbb`. */
export function rgbOf(hex: string): [number, number, number] {
  const value = Number.parseInt(hex.replace('#', ''), 16);
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

/** WCAG 2.x relative luminance. */
export function relativeLuminance(rgb: readonly [number, number, number]): number {
  const channel = (raw: number): number => {
    const c = raw / 255;
    return c <= 0.039_28 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(rgb[0]) + 0.7152 * channel(rgb[1]) + 0.0722 * channel(rgb[2]);
}

/** WCAG 2.x contrast ratio between two colours, order-independent. */
export function contrastRatio(
  a: readonly [number, number, number],
  b: readonly [number, number, number],
): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

/** The token values the bands are measured against, mirrored from `ui/tokens/colors.css`. */
export const INK_1 = '#2B2114';
export const INK_2 = '#5C4A2E';
export const SURFACE_CARD = '#FFFDF6';

/**
 * The three bands `client-home` names, as vertical screen fractions in the portrait layout.
 *
 * The control-label band is measured against the **card**, not the veil, and that is the point of
 * **D-h** rather than a gap in the measurement: the card is opaque precisely so the text down
 * there does not depend on how thick the veil happens to be. Measuring it against the veil would
 * be measuring a surface that is not behind it.
 */
export const LABEL_BANDS = {
  wordmark: { y: 0.1, ink: INK_1, over: 'veil' as const },
  tagline: { y: 0.145, ink: INK_2, over: 'veil' as const },
  controlLabel: { y: 0.78, ink: INK_2, over: 'card' as const },
};

/** The contrast ratio for one named band. */
export function bandContrast(band: keyof typeof LABEL_BANDS): number {
  const spec = LABEL_BANDS[band];
  const background = spec.over === 'card' ? rgbOf(SURFACE_CARD) : veiledBackgroundAt(spec.y);
  return contrastRatio(rgbOf(spec.ink), background);
}
